import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut,
  Lock,
  Briefcase,
  Mail,
  Phone,
  Calendar,
  FileText,
  Trash2,
  CheckCircle,
  Eye,
  Loader2,
  Filter,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'gs_admin_token';

const StatusBadge = ({ status }) => {
  const map = {
    new: { label: 'Nuovo', cls: 'bg-blue-100 text-blue-800' },
    read: { label: 'Letto', cls: 'bg-gray-100 text-gray-700' },
    contacted: { label: 'Contattato', cls: 'bg-green-100 text-green-800' },
  };
  const cfg = map[status] || map.new;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`px-3 py-1 rounded-full text-xs font-semibold ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
};

const LoginForm = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Errore login');
      localStorage.setItem(TOKEN_KEY, json.token);
      onLogin(json.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-100 to-amber-50 px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-10 space-y-6"
      >
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#7f9e60]/10 rounded-full mb-4">
            <Lock className="w-8 h-8 text-[#7f9e60]" />
          </div>
          <h1
            className="text-3xl font-bold text-gray-900"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Area Riservata
          </h1>
          <p className="text-gray-500 text-sm mt-1">Accedi per vedere le candidature</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#7f9e60] focus:ring-2 focus:ring-[#7f9e60]/20 outline-none transition"
            placeholder="••••••••"
          />
        </div>

        {error && (
          <p data-testid="admin-login-error" className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#7f9e60] text-white font-semibold rounded-full hover:bg-[#6a8550] transition disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Accedi'}
        </button>
      </form>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/admin/applications`, { headers });
      if (res.status === 401) {
        onLogout();
        return;
      }
      const json = await res.json();
      setItems(json);
    } catch {
      setError('Impossibile caricare le candidature');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    await fetch(`${API}/admin/applications/${id}`, {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
  };

  const deleteApp = async (id) => {
    if (!window.confirm('Eliminare definitivamente questa candidatura?')) return;
    await fetch(`${API}/admin/applications/${id}`, { method: 'DELETE', headers });
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const viewCv = (id) => {
    window.open(`${API}/admin/applications/${id}/cv?auth=${token}`, '_blank');
  };

  const filtered = items.filter((it) => filter === 'all' || it.status === filter);
  const counts = items.reduce(
    (acc, it) => ({ ...acc, [it.status]: (acc[it.status] || 0) + 1 }),
    {}
  );

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#7f9e60] rounded-full flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1
                className="text-xl font-bold text-gray-900"
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              >
                Candidature Grano e Sale
              </h1>
              <p className="text-xs text-gray-500">{items.length} totali</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
          >
            <LogOut className="w-4 h-4" />
            Esci
          </button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <Filter className="w-4 h-4 text-gray-500" />
          {[
            { k: 'all', label: 'Tutte', n: items.length },
            { k: 'new', label: 'Nuove', n: counts.new || 0 },
            { k: 'read', label: 'Lette', n: counts.read || 0 },
            { k: 'contacted', label: 'Contattate', n: counts.contacted || 0 },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                filter === f.k
                  ? 'bg-[#7f9e60] text-white shadow'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {f.label} ({f.n})
            </button>
          ))}
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#7f9e60]" />
          </div>
        ) : error ? (
          <p className="text-red-600 bg-red-50 p-4 rounded-lg">{error}</p>
        ) : filtered.length === 0 ? (
          <div
            data-testid="empty-state"
            className="bg-white rounded-2xl shadow-sm p-16 text-center"
          >
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nessuna candidatura {filter !== 'all' ? 'in questa categoria' : 'ricevuta finora'}.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="applications-list">
            {filtered.map((it) => (
              <div
                key={it.id}
                data-testid={`application-${it.id}`}
                className="bg-white rounded-2xl shadow-sm hover:shadow-md transition p-6"
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <h3 className="text-lg font-bold text-gray-900">{it.name}</h3>
                      <span className="px-3 py-1 bg-[#7f9e60]/10 text-[#7f9e60] rounded-full text-xs font-semibold">
                        {it.position}
                      </span>
                      <StatusBadge status={it.status} />
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                      <a
                        href={`mailto:${it.email}`}
                        className="flex items-center gap-1.5 hover:text-[#7f9e60]"
                      >
                        <Mail className="w-4 h-4" />
                        {it.email}
                      </a>
                      <a
                        href={`tel:${it.phone}`}
                        className="flex items-center gap-1.5 hover:text-[#7f9e60]"
                      >
                        <Phone className="w-4 h-4" />
                        {it.phone}
                      </a>
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        {new Date(it.created_at).toLocaleString('it-IT', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </span>
                    </div>
                    {it.message && (
                      <p className="text-gray-700 bg-gray-50 rounded-lg p-3 text-sm whitespace-pre-wrap">
                        {it.message}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-row lg:flex-col gap-2 flex-shrink-0">
                    <button
                      onClick={() => viewCv(it.id)}
                      data-testid={`view-cv-${it.id}`}
                      className="flex items-center gap-1.5 px-4 py-2 bg-[#7f9e60] text-white rounded-lg text-sm font-semibold hover:bg-[#6a8550] transition"
                    >
                      <FileText className="w-4 h-4" />
                      Vedi CV
                    </button>
                    {it.status === 'new' && (
                      <button
                        onClick={() => updateStatus(it.id, 'read')}
                        data-testid={`mark-read-${it.id}`}
                        className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition"
                      >
                        <Eye className="w-4 h-4" />
                        Letto
                      </button>
                    )}
                    {it.status !== 'contacted' && (
                      <button
                        onClick={() => updateStatus(it.id, 'contacted')}
                        data-testid={`mark-contacted-${it.id}`}
                        className="flex items-center gap-1.5 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-semibold hover:bg-green-200 transition"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Contattato
                      </button>
                    )}
                    <button
                      onClick={() => deleteApp(it.id)}
                      data-testid={`delete-${it.id}`}
                      className="flex items-center gap-1.5 px-4 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-semibold hover:bg-red-100 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                      Elimina
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    navigate('/admin');
  };

  if (!token) return <LoginForm onLogin={setToken} />;
  return <Dashboard token={token} onLogout={handleLogout} />;
};

export default AdminPage;
